import React, { useMemo } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ChartTooltip from "./ChartTooltip";
import CurrencyFacets from "./CurrencyFacets";
import { CurrencyChip, FacetHeader, FacetStats, FacetWrapper } from "./layout";
import { STATUS_GOOD, STATUS_CRITICAL, TEXT_PRIMARY, GRIDLINE, TEXT_MUTED } from "./palette";
import { formatAmount, formatCompactAmount } from "./format";
import { buildMonthKeys, buildMonthKeysFromRange } from "./monthKeys";

function BalanceDot({ cx, cy, value }) {
  return <circle cx={cx} cy={cy} r={3} fill={value >= 0 ? STATUS_GOOD : STATUS_CRITICAL} />;
}

BalanceDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  value: PropTypes.number,
};

BalanceDot.defaultProps = {
  cx: 0,
  cy: 0,
  value: 0,
};

function MonthTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const point = payload[0].payload;

  return (
    <ChartTooltip
      label={dayjs(`${label}-01`).format("YYYY年M月")}
      value={`收入 ${formatAmount(point.income)}`}
      detail={`支出 ${formatAmount(point.expense)}・結餘 ${formatAmount(point.balance)}`}
    />
  );
}

MonthTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
};

MonthTooltip.defaultProps = {
  active: false,
  payload: [],
  label: "",
};

function CurrencyMonthlyChart({ currencyName, months, rows }) {
  const data = useMemo(() => {
    const byMonth = {};
    rows.forEach((row) => {
      if (!byMonth[row.month]) {
        byMonth[row.month] = { income: 0, expense: 0 };
      }
      byMonth[row.month][row.type === "income" ? "income" : "expense"] = row.total;
    });

    return months.map((month) => {
      const income = (byMonth[month] && byMonth[month].income) || 0;
      const expense = (byMonth[month] && byMonth[month].expense) || 0;
      return { month, income, expense, balance: income - expense };
    });
  }, [months, rows]);

  const { incomeTotal, expenseTotal } = useMemo(
    () =>
      data.reduce(
        (acc, item) => ({
          incomeTotal: acc.incomeTotal + item.income,
          expenseTotal: acc.expenseTotal + item.expense,
        }),
        { incomeTotal: 0, expenseTotal: 0 }
      ),
    [data]
  );

  return (
    <FacetWrapper>
      <FacetHeader>
        <CurrencyChip>{currencyName}</CurrencyChip>
        <FacetStats>
          {`收入共 ${formatAmount(incomeTotal)}・支出共 ${formatAmount(expenseTotal)}`}
        </FacetStats>
      </FacetHeader>
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={GRIDLINE} />
          <XAxis
            dataKey="month"
            tickFormatter={(month) => dayjs(`${month}-01`).format("M月")}
            tick={{ fill: TEXT_MUTED, fontSize: 11 }}
            axisLine={{ stroke: GRIDLINE }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatCompactAmount}
            tick={{ fill: TEXT_MUTED, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <Tooltip content={<MonthTooltip />} cursor={{ fill: "rgba(11,11,11,0.04)" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar
            dataKey="income"
            name="收入"
            fill={STATUS_GOOD}
            radius={[4, 4, 0, 0]}
            maxBarSize={20}
          />
          <Bar
            dataKey="expense"
            name="支出"
            fill={STATUS_CRITICAL}
            radius={[4, 4, 0, 0]}
            maxBarSize={20}
          />
          <Line
            dataKey="balance"
            name="結餘"
            stroke={TEXT_PRIMARY}
            strokeWidth={2}
            dot={<BalanceDot />}
            activeDot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </FacetWrapper>
  );
}

CurrencyMonthlyChart.propTypes = {
  currencyName: PropTypes.string.isRequired,
  months: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default function MonthlySpendingChart({ monthlySpending, months, startDate, endDate }) {
  const monthKeys = useMemo(
    () =>
      startDate && endDate ? buildMonthKeysFromRange(startDate, endDate) : buildMonthKeys(months),
    [months, startDate, endDate]
  );

  return (
    <CurrencyFacets rows={monthlySpending}>
      {(currency, rows) => (
        <CurrencyMonthlyChart
          key={currency.id}
          currencyName={currency.name}
          months={monthKeys}
          rows={rows}
        />
      )}
    </CurrencyFacets>
  );
}

MonthlySpendingChart.propTypes = {
  monthlySpending: PropTypes.arrayOf(PropTypes.object),
  months: PropTypes.number,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

MonthlySpendingChart.defaultProps = {
  monthlySpending: null,
  months: undefined,
  startDate: null,
  endDate: null,
};
